import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { CatalogItemBase } from "building-types/entities/catalogItemBase";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { MaterialSrcOverride } from "building-types/materials/materialTypes";
import type { FormulaDisabled } from "building-types/options/formulaDisabled";
import type { Table } from "common/data/table";
import type { MaterialSrc } from "common/materials/materialSrc";
import type { Extrusion, Face } from "geom/brep";
import type GeomTagGroup from "geom/geomTagGroup";
import type { OBB2 } from "geom/obb2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import type { DocForeignKeyTable } from "../doc/docForeignKeyTable";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { RailingViewNoteTable } from "./viewNotesSettings";

export interface Railing extends DocEntityBase<"RAILING">, RailingBase, LabelableEntityBase {
  readonly id: PrimaryEntityId<"RAILING">;
}

export type RailingSetting = RailingDefaultSetting; // | SomeOtherRailingSetting

export enum RailingSettingsType {
  RAILING_DEFAULT = "RAILING_DEFAULT",
}

export interface RailingBase extends FormulaDisabled {
  readonly levelId: ForeignEntityId<"LEVEL">;
  readonly ignoreStairsAndRoomsAbove?: boolean;
  readonly useNewRailingTerminationLogic?: boolean;
  // Can be an empty object. Use `railingGetCleanPath` to get a clean path.
  // Use 'railingHasPath' to check if path is valid.
  readonly path: PolyLine2 | null;
  readonly railingSettingId: ForeignEntityId<"RAILING_SETTING">;
  readonly settingsOverride?: Partial<RailingSetting>;
}

export interface RailingDrawingSettings {
  readonly includeInPlanDrawing: boolean;
  readonly includeInShowRoomPlan: boolean;
  readonly includeInBrochurePlan: boolean;
}

export interface RailingDefaultSetting extends CatalogItemBase<"RAILING_SETTING">, DocEntityBase<"RAILING_SETTING"> {
  readonly type: RailingSettingsType.RAILING_DEFAULT;
  readonly id: PrimaryEntityId<"RAILING_SETTING">;
  readonly name: string;
  readonly sortIndex: number;
  readonly hasTopRail: boolean;
  readonly topRailHeight?: number; //to top of rail
  readonly topRail?: Rail;
  readonly topRailMaterial?: MaterialSrc;

  readonly hasBottomRail: boolean;
  readonly bottomRailHeight?: number; //to bottom of rail
  readonly bottomRail?: Rail;
  readonly bottomRailMaterial?: MaterialSrc;
  readonly includeRailInPlanDrawing?: boolean;
  readonly includeRailInShowRoomPlan?: boolean;
  readonly includeRailInBrochurePlan?: boolean;

  readonly hasPosts: boolean;
  readonly removeTransitionPosts?: boolean;
  readonly removeStartPost: boolean;
  readonly removeEndPost: boolean;
  readonly postHeight?: number;
  readonly post?: Post;
  readonly postMaterial?: MaterialSrc;
  readonly includePostsInPlanDrawing?: boolean;
  readonly includePostsInShowRoomPlan?: boolean;
  readonly includePostsInBrochurePlan?: boolean;

  readonly infillStyle: RailingInfillStyle;
  readonly infillDepth?: number;
  readonly baluster?: Baluster;
  readonly infillMaterial?: MaterialSrc;

  readonly viewNotes?: RailingViewNoteTable;
}

export enum RailingInfillStyle {
  BALUSTERS = "BALUSTERS",
  SOLID = "SOLID",
  NONE = "NONE",
}

export enum RailType {
  RAIL_DEFAULT = "RAIL_DEFAULT",
}

export type Rail = RailDefault;

export interface RailDefault {
  readonly type: RailType.RAIL_DEFAULT;
  readonly width: number;
  readonly thickness: number;
}

export const DefaultRail: Rail = {
  type: RailType.RAIL_DEFAULT,
  width: 4,
  thickness: 1.5,
};

export enum PostType {
  POST_DEFAULT = "POST_DEFAULT",
}

export type Post = PostDefault;

export interface PostDefault {
  readonly type: PostType.POST_DEFAULT;
  readonly width: number;
  readonly depth: number;
}

export const DefaultPost: Post = {
  type: PostType.POST_DEFAULT,
  width: 3,
  depth: 3,
};

export type Baluster = BalusterDefault;

export enum BalusterType {
  BALUSTER_DEFAULT = "BALUSTER_DEFAULT",
}

export interface BalusterDefault {
  readonly type: BalusterType.BALUSTER_DEFAULT;
  readonly width: number;
  readonly spacing: number;
  readonly startHeight: number;
}

export const DefaultBaluster: BalusterDefault = {
  type: BalusterType.BALUSTER_DEFAULT,
  width: 1.5,
  spacing: 5,
  startHeight: 5.5,
};

export type GeneratedRailing = GeneratedRailingDefault;

export interface PostLinework2 {
  id?: string;
  polyLine2: PolyLine2;
  obb: OBB2;
}
export interface RailingLinework {
  readonly levelId: ForeignEntityId<"LEVEL">;
  polyLine2s: PolyLine2[];
  polyLine2sAboveViewCut: PolyLine2[];
  postPolyLine2s: PostLinework2[];
  obbs: OBB2[];
  obbsAboveViewCut: OBB2[];
}

export type RailingPostPlacementType = "START" | "END" | "TRANSITION";

export interface RailingPostTag extends MaterialSrcOverride {
  readonly type: "POST";
  readonly placementType: RailingPostPlacementType;
}

export type RailingRailType = "TOP" | "BOTTOM";

/**
 * One reportable run of a rail, and the slice of the generated centerline it covers. A run normally
 * spans a single centerline edge; a gap piece bridging a height change keeps every one of its edges on
 * the run it interrupts, so `edgeCount` records how many centerline edges the run's faces are tagged with.
 */
export interface RailingRailSegment {
  readonly segmentIndex: number;
  readonly centerLine: PolyLine3;
  readonly edgeCount: number;
}

export interface RailingRailTag extends MaterialSrcOverride {
  readonly type: "RAIL";
  readonly railType: RailingRailType;
  readonly width: number;
  readonly thickness: number;
  readonly height: number;
  readonly segments: readonly RailingRailSegment[];
}

export interface RailingInfillTag extends MaterialSrcOverride {
  readonly type: "INFILL";
  readonly infillType: RailingInfillStyle.BALUSTERS | RailingInfillStyle.SOLID;
  readonly segmentIndex: number;
}

export type RailingGeomTag = RailingPostTag | RailingRailTag | RailingInfillTag;

export interface RailingGeomGroup {
  readonly group: GeomTagGroup<RailingGeomTag>;
  readonly pickHandle: DocEntityHandle;
}

export interface GeneratedRailingDefault {
  readonly type: RailingSettingsType.RAILING_DEFAULT;
  readonly id: string;
  readonly instanceId?: ForeignEntityId<"COMPONENT_INSTANCE">;
  readonly levelId: ForeignEntityId<"LEVEL">;
  readonly baseSettingName: string;
  readonly path: PolyLine2;
  readonly resolvedPath3s: readonly (PolyLine3 | undefined)[];
  readonly railWidth: number;
  readonly infillStyle: RailingInfillStyle;
  readonly pathThickened: PolyLine2;
  readonly railingLinework2: DocForeignKeyTable<"RAILING", RailingLinework>;
  readonly pickHandle: DocEntityHandle;
  readonly groups: RailingGeomGroup[];
  readonly associatedWithStairs: boolean;
  readonly terminationLogicWouldChange: boolean;
  readonly postPlacements: Table<PostPlacement>;
  readonly includeRailInPlanDrawing: boolean;
  readonly includeRailInShowRoomPlan: boolean;
  readonly includeRailInBrochurePlan: boolean;
  readonly includePostsInPlanDrawing?: boolean;
  readonly includePostsInShowRoomPlan?: boolean;
  readonly includePostsInBrochurePlan?: boolean;

  // These faces are used for the building outline and also to check if the railing is inside the building for lighting purposes.
  readonly railingPathFacesForBuildingOutlineAndLighting: Face[];

  // These extrusions are used for the building outline.
  readonly buildingOutlineExtrusions: Extrusion[];
}

export enum PostPlacementType {
  PARAM = "PARAM",
  ARRAY = "ARRAY",
  DIVISION = "DIVISION",
  MAX_DISTANCE = "MAX_DISTANCE",
}

export interface PostPlacementBase extends DocEntityBase<"RAILING_POST"> {
  readonly id: PrimaryEntityId<"RAILING_POST">;
  readonly entityType: "RAILING_POST";
  readonly type: PostPlacementType;
  readonly parentId: DocEntityParentId<"RAILING">;
  readonly edgeIndex?: number;
}

export type PostPlacement = ParamPostPlacement | ArrayPostPlacement | DivisionPostPlacement | MaxDistancePostPlacement;

export interface ParamPostPlacement extends PostPlacementBase {
  readonly type: PostPlacementType.PARAM;
  readonly param: number;
  readonly paramNormalized: boolean;
  readonly removePostsFromSettings: boolean;
  readonly removeInfillBefore: boolean;
  readonly removeInfillAfter: boolean;
}

export interface ArrayPostPlacement extends PostPlacementBase {
  readonly type: PostPlacementType.ARRAY;
  readonly spacing: number;
  readonly shift: number;
  readonly maxCount?: number;
  readonly removePostsFromSettings: boolean;
}

export interface DivisionPostPlacement extends PostPlacementBase {
  readonly type: PostPlacementType.DIVISION;
  readonly segments: number;
  readonly removePostsFromSettings: boolean;
  readonly includeStartPost: boolean;
}

export interface MaxDistancePostPlacement extends PostPlacementBase {
  readonly type: PostPlacementType.MAX_DISTANCE;
  readonly maxDistance: number;
  readonly removePostsFromSettings: boolean;
  readonly includeStartPost: boolean;
}

export interface PostPlacementInstance {
  readonly edgeIndex: number;
  readonly x: number;
  readonly y: number;
  readonly tx: number;
  readonly ty: number;
  readonly param: number; // denormalized param
  readonly id?: string; // id is populated when manually authored post
  readonly removeInfillBefore: boolean;
  readonly removeInfillAfter: boolean;
}
